"use client";

import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { fetchCommunityHighlightedSuppliers } from "@/lib/community/api-client";
import { ShopCard } from "./ShopCard";
import { useNearViewport } from "./useNearViewport";

interface HighlightedSuppliersSectionProps {
  onSeeAll?: () => void;
}

export function HighlightedSuppliersSection({
  onSeeAll,
}: HighlightedSuppliersSectionProps) {
  const { targetRef, isNearViewport } = useNearViewport();
  const { data, isLoading, error } = useQuery({
    queryKey: ["community-highlighted-suppliers"],
    queryFn: fetchCommunityHighlightedSuppliers,
    enabled: isNearViewport,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const shops = data?.shops ?? [];
  const visibleShops = shops.filter(
    (shop) =>
      (shop.total_products ?? 0) > 0 && (shop.products?.length ?? 0) > 0,
  );

  if (isNearViewport && !isLoading && !error && visibleShops.length === 0) {
    return null;
  }

  return (
    <div ref={targetRef} className="mb-12 min-h-48">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-h3 text-ink">Hand-picked brands</h2>
        {onSeeAll && (
          <Button variant="ghost" size="sm" onClick={onSeeAll}>
            See all →
          </Button>
        )}
      </div>
      {error ? (
        <p className="text-muted">Failed to load hand-picked brands.</p>
      ) : (
        <div className="space-y-4">
          {visibleShops.map((shop) => (
            <ShopCard key={shop.id} shop={shop} />
          ))}
          {(!isNearViewport || isLoading) &&
            Array.from({ length: 2 }).map((_, index) => (
              <Card
                key={`highlighted-skeleton-${index}`}
                className="h-64 animate-pulse bg-raised/70"
              />
            ))}
        </div>
      )}
    </div>
  );
}
